import { useCallback, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller, useForm } from "react-hook-form";
import { FormCPFMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_cpf_masked_input";
import { DependenteForm, DependenteSchema } from "../../../schemas/Funcionarios_cadastro/dependente_schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dependente } from "../../../../domain/models/dependente";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import dayjs from "dayjs";

/**
 * CadastroDependenteModal
 */

type Props = {
  saving: boolean,
  open: boolean,
  selectedDependente: Dependente | null,
  onSubmit: (form: DependenteForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
};

export const CadastroDependenteModal = ({
  saving,
  open,
  selectedDependente,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const hookForm = useForm<DependenteForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DependenteSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedDependente) {
        hookForm.setValue('nome', selectedDependente.nome);
        hookForm.setValue('grauParentesco', selectedDependente.grauParentesco);
        hookForm.setValue('dataNascimento', selectedDependente.dataNascimento ? dayjs(selectedDependente.dataNascimento).toDate() : null);
        hookForm.setValue('rg', selectedDependente.rg);
        hookForm.setValue('cpf', selectedDependente.cpf);
      } else {
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    open,
    selectedDependente,
  ]);

  const handleSubmit = useCallback((form: DependenteForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleChangeHistory = useCallback(() => {
    if (selectedDependente) {
      onOpenChangeHistory(selectedDependente.idDependente, LogTipoTabela.FUNCIONARIO_DEPENDENTE);
    }
  }, [selectedDependente, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        Cadastrar Dependente
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <DefaultFormInput
                name='nome'
                label='Nome'
                required={true}
                readOnly={saving}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name={'grauParentesco'}
                label={"Grau de Parentesco"}
                readOnly={saving}
                reactHookForm={hookForm} />

              <Controller
                name='dataNascimento'
                control={hookForm.control}
                render={({ field }) => {
                  return <DatePicker
                    readOnly={saving}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    label="Data de Nascimento"
                    defaultValue={null}
                    value={field.value ? dayjs.utc(field.value as any).local() : null}
                    onChange={e => field.onChange(e !== null ? e.startOf('day').utc().toDate() : null)} />
                }}
              />

              <DefaultFormInput
                name='rg'
                label='RG'
                readOnly={saving}
                reactHookForm={hookForm} />

              <FormCPFMaskedInput
                name='cpf'
                label='CPF'
                readOnly={saving}
                reactHookForm={hookForm} />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedDependente) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}